import { useState, useRef } from "react";
import {
  ProductTableImage,
  ProductTableListWrapper,
  ProductTableSeatToggle,
  ProductTableSeatToggleWrapper,
  ProductTableSelectTableBackground,
  ProductTableSelectTableListWrapper,
  ProductTableSelectTableWrapper,
  ProductTableSelectTableItem,
  ProductTableTitle,
  ProductTableWrapper,
} from "./styles/product-table";
interface ProductTableProps {
  seat: number | null;
  setSeat: (seat: number | null) => void;
  table: string | null;
  setTable: (table: string | null) => void;
}
const tableList = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "FINAL"];

// 각 테이블의 너비 정의
const getTableWidth = (tableItem: string) => {
  return tableItem === "FINAL" ? 150 : 47;
};

// 각 테이블의 중심점까지의 누적 거리 계산
const getTableCenterOffset = (index: number) => {
  let offset = 0;
  for (let i = 0; i < index; i++) {
    offset += getTableWidth(tableList[i]);
  }
  return offset + getTableWidth(tableList[index]) / 2;
};

const ProductTable = ({
  seat,
  setSeat,
  table,
  setTable,
}: ProductTableProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedIndex = tableList.indexOf(table || "A");

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
    setCurrentOffset(0);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const offset = e.clientX - startX;
    setCurrentOffset(offset);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;

    // 현재 선택된 테이블의 중심점
    const currentCenterOffset = getTableCenterOffset(selectedIndex);

    // 드래그 후의 예상 중심점
    const newCenterOffset = currentCenterOffset - currentOffset;

    // 가장 가까운 테이블 찾기
    let closestIndex = 0;
    let minDistance = Math.abs(getTableCenterOffset(0) - newCenterOffset);

    for (let i = 1; i < tableList.length; i++) {
      const distance = Math.abs(getTableCenterOffset(i) - newCenterOffset);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }

    if (closestIndex !== selectedIndex) {
      setTable(tableList[closestIndex]);
    }

    setIsDragging(false);
    setCurrentOffset(0);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setCurrentOffset(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const offset = e.touches[0].clientX - startX;
    setCurrentOffset(offset);
  };

  const handleTouchEnd = () => {
    handleMouseUp();
  };
  return (
    <ProductTableWrapper>
      <ProductTableTitle>테이블 선택</ProductTableTitle>
      <ProductTableListWrapper>
        <ProductTableImage src="/image-web/order/table.png" alt="table" />
        <ProductTableSeatToggleWrapper width="158px">
          <ProductTableSeatToggle
            selected={seat === 2}
            onClick={() => setSeat(2)}
          >
            2
          </ProductTableSeatToggle>
          <ProductTableSeatToggle
            selected={seat === 3}
            onClick={() => setSeat(3)}
          >
            3
          </ProductTableSeatToggle>
        </ProductTableSeatToggleWrapper>
        <ProductTableSeatToggleWrapper width="284px">
          <ProductTableSeatToggle
            selected={seat === 1}
            onClick={() => setSeat(1)}
          >
            1
          </ProductTableSeatToggle>
          <ProductTableSeatToggle
            selected={seat === 4}
            onClick={() => setSeat(4)}
          >
            4
          </ProductTableSeatToggle>
        </ProductTableSeatToggleWrapper>
        <ProductTableSeatToggleWrapper width="284px">
          <ProductTableSeatToggle
            selected={seat === 10}
            onClick={() => setSeat(10)}
          >
            10
          </ProductTableSeatToggle>
          <ProductTableSeatToggle
            selected={seat === 5}
            onClick={() => setSeat(5)}
          >
            5
          </ProductTableSeatToggle>
        </ProductTableSeatToggleWrapper>
        <ProductTableSeatToggleWrapper width="284px">
          <ProductTableSeatToggle
            selected={seat === 9}
            onClick={() => setSeat(9)}
          >
            9
          </ProductTableSeatToggle>
          <ProductTableSeatToggle
            selected={seat === 6}
            onClick={() => setSeat(6)}
          >
            6
          </ProductTableSeatToggle>
        </ProductTableSeatToggleWrapper>
        <ProductTableSeatToggleWrapper width="158px">
          <ProductTableSeatToggle
            selected={seat === 8}
            onClick={() => setSeat(8)}
          >
            8
          </ProductTableSeatToggle>
          <ProductTableSeatToggle
            selected={seat === 7}
            onClick={() => setSeat(7)}
          >
            7
          </ProductTableSeatToggle>
        </ProductTableSeatToggleWrapper>
      </ProductTableListWrapper>
      <ProductTableSelectTableWrapper>
        <div className="title">테이블</div>
        <ProductTableSelectTableListWrapper
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <ProductTableSelectTableBackground
            width={getTableWidth(tableList[selectedIndex])}
          />
          {tableList.map((tableItem, index) => {
            // 선택된 테이블의 중심점을 기준으로 각 테이블의 위치 계산
            const selectedCenterOffset = getTableCenterOffset(selectedIndex);
            const itemCenterOffset = getTableCenterOffset(index);
            const baseOffset = itemCenterOffset - selectedCenterOffset;
            const dragOffset = isDragging ? currentOffset : 0;
            const finalOffset = baseOffset + dragOffset;

            // 각 아이템의 너비의 절반만큼 왼쪽으로 이동하여 중심 정렬
            const itemWidth = getTableWidth(tableItem);
            const translateX = finalOffset - itemWidth / 2;

            return (
              <ProductTableSelectTableItem
                key={`${tableItem}-${index}`}
                isSelected={index === selectedIndex}
                width={itemWidth}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: 0,
                  transform: `translateX(${translateX}px)`,
                  transition: isDragging ? "none" : "transform 0.3s ease",
                  zIndex: index === selectedIndex ? 2 : 1,
                }}
                onClick={() => {
                  if (!isDragging) {
                    setTable(tableItem);
                  }
                }}
              >
                {tableItem}
              </ProductTableSelectTableItem>
            );
          })}
        </ProductTableSelectTableListWrapper>
      </ProductTableSelectTableWrapper>
    </ProductTableWrapper>
  );
};

export default ProductTable;
